/**
 * useAdSetFieldRules — Derives field visibility/lock/autofill rules
 * based on campaign objective + ad set metadata.
 *
 * Rules are purely frontend rendering concerns — no data model changes.
 */

import type { AdSet, Campaign } from "@/types/bulkAd";

/** Destination types where a URL is required (off-platform destinations). */
export const URL_REQUIRED_DESTINATIONS = ["WEBSITE", "APP", "APPLINKS_AUTOMATIC", "SHOP_AUTOMATIC"] as const;

export interface FieldRules {
  // Hidden fields (should not render)
  hideUrl: boolean;
  hideLeadgenForm: boolean;
  hideProductTags: boolean;
  hideProductSet: boolean;
  hideCatalogFields: boolean;

  // Locked fields (read-only, gray background, auto-filled from ad set)
  lockFacebookPage: boolean;
  lockedFacebookPageId: string | null;
  lockInstagramAccount: boolean;

  // Page Likes context (ON_PAGE destination)
  isPageLikes: boolean;

  // Page Likes — additional locks/hides
  lockAdFormat: boolean;
  hideHeadline: boolean;
  hideLinkUrl: boolean;
  hideDisplayLink: boolean;
  hideDescription: boolean;
  hideCta: boolean;
  hideUtmParameters: boolean;
  hideCatalog: boolean;
  singleAssetOnly: boolean;

  // URL behavior
  urlRequired: boolean;
  urlAutoValue: string | null; // e.g. "http://fb.me/" for OUTCOME_LEADS + ON_AD

  // CTA behavior
  autoSelectNoButton: boolean;

  // URL visibility based on CTA selection (on-platform ads only)
  showUrlForCta: boolean;

  // Leadgen form behavior
  leadgenFormRequired: boolean;
}

const DEFAULT_RULES: FieldRules = {
  hideUrl: false,
  hideLeadgenForm: false,
  hideProductTags: false,
  hideProductSet: false,
  hideCatalogFields: false,
  lockFacebookPage: false,
  lockedFacebookPageId: null,
  lockInstagramAccount: false,
  isPageLikes: false,
  lockAdFormat: false,
  hideHeadline: false,
  hideLinkUrl: false,
  hideDisplayLink: false,
  hideDescription: false,
  hideCta: false,
  hideUtmParameters: false,
  hideCatalog: false,
  singleAssetOnly: false,
  urlRequired: false,
  urlAutoValue: null,
  autoSelectNoButton: false,
  showUrlForCta: false,
  leadgenFormRequired: false,
};

/**
 * Derive field rules from campaign objective and ad set metadata.
 * Returns DEFAULT_RULES (everything visible, nothing locked) when no ad set is selected.
 */
export function getFieldRules(
  campaign: Campaign | null,
  adSet: AdSet | null,
  ctaValue?: string | null,
): FieldRules {
  // No ad set = no restrictions
  if (!adSet || !campaign) return { ...DEFAULT_RULES };

  const objective = campaign.objective || "";
  const destinationType = adSet.destinationType || "";
  const promotedObject = adSet.promotedObject;

  const rules: FieldRules = { ...DEFAULT_RULES };

  // === Auto-fill + Lock rules from promoted_object ===
  if (promotedObject?.page_id) {
    rules.lockFacebookPage = true;
    rules.lockedFacebookPageId = promotedObject.page_id as string;
  }

  // === Page Likes detection ===
  if (destinationType === "ON_PAGE") {
    rules.isPageLikes = true;
    rules.lockInstagramAccount = true;
    rules.lockAdFormat = true;
    rules.hideHeadline = true;
    rules.hideLinkUrl = true;
    rules.hideDisplayLink = true;
    rules.hideDescription = true;
    rules.hideCta = true;
    rules.hideUtmParameters = true;
    rules.hideCatalog = true;
    rules.hideProductTags = true;
    rules.hideProductSet = true;
    rules.hideLeadgenForm = true;
    rules.singleAssetOnly = true;
  }

  // === Hide rules based on campaign objective ===

  // Treat "UNDEFINED" destination_type as empty
  const destType = destinationType === "UNDEFINED" ? "" : destinationType;

  // On-platform destinations: check CTA to determine URL visibility
  const isOnPlatform = !URL_REQUIRED_DESTINATIONS.includes(destType as any);
  if (isOnPlatform && destType !== "") {
    rules.autoSelectNoButton = true;
  }
  // Compute showUrlForCta for ANY on-platform destination (including empty destType)
  if (isOnPlatform && ctaValue && ctaValue !== "" && ctaValue !== "NO_BUTTON") {
    rules.showUrlForCta = true;
  }

  switch (objective) {
    case "OUTCOME_AWARENESS":
      // URL visibility depends on CTA selection (same as on-platform engagement)
      rules.hideUrl = !rules.showUrlForCta;
      rules.hideLeadgenForm = true;
      rules.hideProductTags = true;
      rules.hideProductSet = true;
      rules.hideCatalogFields = true;
      break;

    case "OUTCOME_TRAFFIC":
      if (URL_REQUIRED_DESTINATIONS.includes(destType as any)) {
        rules.urlRequired = true;
      }
      if (!URL_REQUIRED_DESTINATIONS.includes(destType as any)) {
        rules.hideLeadgenForm = true;
      }
      break;

    case "OUTCOME_ENGAGEMENT":
      if (URL_REQUIRED_DESTINATIONS.includes(destType as any)) {
        rules.urlRequired = true;
      } else if (destType === "ON_PAGE") {
        // Page Likes — no URL needed, page_id handled at ad set level
        rules.hideUrl = true;
        rules.hideLeadgenForm = true;
      } else {
        // On-platform destination — URL visibility depends on CTA selection
        rules.hideUrl = !rules.showUrlForCta;
      }
      break;

    case "OUTCOME_LEADS":
      if (!URL_REQUIRED_DESTINATIONS.includes(destType as any)) {
        rules.leadgenFormRequired = true;
        rules.urlAutoValue = "http://fb.me/";
      }
      break;

    case "OUTCOME_SALES":
      // No changes — everything visible (current behavior)
      break;
  }

  console.log('[getFieldRules]', { objective, destinationType, ctaValue, hideUrl: rules.hideUrl, showUrlForCta: rules.showUrlForCta });

  return rules;
}
